// =============================================================================
// features/sales/campaigns/detail/CampaignDetailStates.tsx — Estados de nível
// de página (sem permissão, flag desligada, carregando, não encontrada, erro
// de rede). Mesmo padrão visual de CampaignsPage.tsx/OperatePage.tsx
// (F35-S07/F35-S10) — DoD do slot: "Campanha fora do escopo do usuário
// resulta em 404, não em tela vazia".
// =============================================================================

import * as React from 'react';

import { Button } from '../../../../components/ui/Button';

export function CampaignDetailNoPermissionState(): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
      <p className="font-sans text-sm text-ink-3 max-w-xs">
        Você não tem acesso às campanhas comerciais. Fale com um administrador se precisar de
        acesso.
      </p>
    </div>
  );
}

export function CampaignDetailFlagDisabledState({
  flagKey,
}: {
  flagKey: string;
}): React.JSX.Element {
  return (
    <div
      className="flex items-start gap-3 px-4 py-3 rounded-sm border"
      style={{ background: 'var(--warning-bg)', borderColor: 'var(--warning)', borderLeftWidth: 3 }}
      role="alert"
    >
      <div>
        <p className="font-sans text-sm font-semibold text-ink">Módulo desabilitado</p>
        <p className="font-sans text-xs text-ink-3 mt-0.5">
          A flag <code className="font-mono">{flagKey}</code> está desativada para esta organização.
        </p>
      </div>
    </div>
  );
}

export function CampaignDetailLoadingState(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <div
        className="h-10 w-2/3 rounded-md animate-pulse"
        style={{ background: 'var(--surface-muted)' }}
        aria-hidden="true"
      />
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="h-16 rounded-md animate-pulse"
          style={{ background: 'var(--surface-muted)' }}
          aria-hidden="true"
        />
      ))}
    </div>
  );
}

export function CampaignDetailMissingIdState(): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
      <p className="font-sans text-sm text-ink-3 max-w-xs">Campanha não informada na URL.</p>
    </div>
  );
}

export function CampaignDetailNotFoundState({ onBack }: { onBack: () => void }): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4 text-center px-4">
      <svg
        viewBox="0 0 48 48"
        fill="none"
        stroke="currentColor"
        strokeWidth={1}
        className="w-12 h-12 text-ink-4"
        aria-hidden="true"
      >
        <rect x="6" y="12" width="36" height="26" rx="3" />
        <path d="M6 20h36M14 8v8M34 8v8" strokeLinecap="round" />
        <path d="M18 24l12 8M30 24l-12 8" strokeLinecap="round" />
      </svg>
      <div className="flex flex-col gap-1">
        <p
          className="font-display font-semibold text-ink"
          style={{ fontSize: 'var(--text-base)', letterSpacing: '-0.02em' }}
        >
          Campanha não encontrada
        </p>
        <p className="font-sans text-sm text-ink-3 max-w-sm">
          Ela pode ter sido removida ou está fora do seu escopo de acesso.
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={onBack}>
        Voltar para campanhas
      </Button>
    </div>
  );
}

export function CampaignDetailErrorState({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-4 text-center px-4">
      <svg
        viewBox="0 0 48 48"
        fill="none"
        stroke="currentColor"
        strokeWidth={1}
        className="w-12 h-12 text-danger"
        aria-hidden="true"
      >
        <circle cx="24" cy="24" r="18" />
        <path d="M24 16v10M24 30v2" strokeLinecap="round" />
      </svg>
      <div className="flex flex-col gap-1">
        <p
          className="font-display font-semibold text-ink"
          style={{ fontSize: 'var(--text-base)', letterSpacing: '-0.02em' }}
        >
          Não foi possível carregar esta campanha
        </p>
        <p className="font-sans text-sm text-ink-3 max-w-xs">
          Verifique sua conexão e tente novamente.
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={onRetry}>
        Tentar novamente
      </Button>
    </div>
  );
}
